import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import HeroCarousel from '../components/HeroCarousel';
import SectionHeading from '../components/SectionHeading';
import Tilt from '../components/Tilt';
import { useContent } from '../content/ContentContext';
import { getIcon } from '../content/icons';
import { fetchNewsList } from '../news/api';

// 主打产品：神秘电波 + 点阵魔方（苹果式产品屏——标题讲价值，图讲产品）
const defaultFeatured = {
  items: [
    {
      key: 'morse',
      theme: 'dark',
      kicker: '主线一 · 神秘电波',
      title: '一节课，打出第一段电码',
      morse: '··· ——— ···',
      tagline: '按压时长区分"点"与"划"，孩子的手指就是发报机。屏幕实时解码，下课就能演示给家长看。',
      path: ['一节体验课', '编码与解码', '信号与通信'],
      specs: ['完全离线', '触摸输入', 'Mixly 图形化', 'Arduino 主控'],
      image: '/images/site/morse-kit.jpg',
    },
    {
      key: 'matrix',
      theme: 'light',
      kicker: '主线二 · Matrix Cube 点阵魔方',
      title: '第一行代码，点亮 64 颗灯',
      tagline: '8×8 LED 点阵是孩子的第一块"屏幕"。积木式编程拖一拖，图案、动画、小游戏立刻亮起来。',
      path: ['点亮第一颗灯', '图案与动画', '模块化联动'],
      specs: ['8×8 LED 点阵', '图形化编程', '模块化扩展', '多面联动'],
      image: '/images/products/matrix-cube.png',
    },
  ],
};

const defaultProofImages = [
  { src: '/images/site/classroom-wide.jpg', title: '课堂实拍', desc: '项目台、硬件套件与课程流程支持机构组织课堂实施。' },
  { src: '/images/team/morse-demo.jpg', title: '摩斯电码课程', desc: '从触摸输入、点划编码到屏幕反馈，形成可交付的项目式课程内容。' },
  { src: '/images/site/ball-balance-device.jpg', title: '板球平衡系统', desc: '完整装置展示机械结构、舵机控制和反馈调节的基本逻辑。' },
];

const defaultProductLines = [
  {
    name: 'Matrix Cube 编程点阵魔方',
    image: '/images/products/matrix-cube.png',
    eyebrow: '图形化编程 / 点阵显示',
    desc: '面向入门阶段的编程点阵魔方课程套件，用 8x8 LED 点阵承载图形化编程、创意交互和课程任务。',
    tags: ['点阵显示', '图形化编程', '模块化扩展'],
  },
  {
    name: '小球平衡综合套件',
    image: '/images/products/infrared-ball-balance.png',
    eyebrow: '红外遥控 / 神秘电波 / 电子画画 / 小球平衡',
    desc: '神秘电波、电子画画和小球平衡归入同一套触控与控制课程线，从输入反馈、红外遥控逐步走向闭环控制。',
    tags: ['红外遥控', '电子画板', '闭环控制'],
  },
  {
    name: '桌面机械臂',
    image: '/images/products/robotic-arm.png',
    eyebrow: '机器人控制 / 运动规划',
    desc: '用于机械结构、舵机控制和空间运动理解的进阶机器人产品，适合社团、实验室和项目展示。',
    tags: ['机械结构', '舵机控制', '项目实训'],
  },
  {
    name: '仿生灵巧手',
    image: '/images/products/dexterous-hand.png',
    eyebrow: '仿生结构 / 多自由度控制',
    desc: '面向高阶机器人课程的灵巧手产品，用多关节结构展示抓握、牵引和复杂动作控制。',
    tags: ['仿生结构', '多关节控制', '机器人进阶'],
  },
];

const fmt = (d) => (d || '').slice(0, 10);

const cardImage = (src = '') => src
  .replace('news-1782815639549-91275285-enhanced.jpg', 'news-1782815639549-91275285-card.jpg')
  .replace('news-1782815630502-7793fb63-enhanced.jpg', 'news-1782815630502-7793fb63-card.jpg');

export default function Home() {
  const { home = {}, site = {} } = useContent();
  const contact = home.contact || { title: '获取机构合作方案', desc: '把真实项目带进课堂。' };
  const proof = home.proof || {};
  const proofImages = proof.items?.length ? proof.items : defaultProofImages;
  const productLines = home.products?.items?.length ? home.products.items : defaultProductLines;
  const featured = home.featured?.items?.length ? home.featured : defaultFeatured;
  const [news, setNews] = useState([]);

  useEffect(() => {
    fetchNewsList({ page: 1, pageSize: 3 }).then((r) => setNews(r.items || [])).catch(() => {});
  }, []);

  return (
    <div>
      <HeroCarousel slides={home.slides} />

      {/* 主打产品：苹果式产品屏，通栏深浅交替 */}
      {featured.items.map((p) => (
        <section key={p.key || p.kicker} className={`product-stage ${p.theme === 'dark' ? 'product-stage--dark' : 'product-stage--light'}`}>
          <div className="reveal">
            <div className="stage-kicker">{p.kicker}</div>
            <h3>{p.title}</h3>
            {p.morse && (
              <div className="stage-morse" aria-hidden="true">
                <span className="morse-blink">{p.morse}</span>
              </div>
            )}
            <p className="stage-tagline">{p.tagline}</p>
            <div className="stage-links">
              <Link to="/courses">了解课程 <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/contact">预约体验课 <ArrowRight className="h-4 w-4" /></Link>
            </div>
            {p.path?.length > 0 && (
              <div className="stage-path" aria-label="课程主线路径">
                {p.path.map((step, si) => (
                  <React.Fragment key={step}>
                    {si > 0 && <span className="stage-path-arrow">→</span>}
                    <span className="stage-path-step">{step}</span>
                  </React.Fragment>
                ))}
              </div>
            )}
            {p.specs?.length > 0 && (
              <div className="stage-specs">
                {p.specs.map((s) => <span key={s}>{s}</span>)}
              </div>
            )}
          </div>
          <div className="stage-media reveal">
            <img src={p.image} alt={p.kicker} />
          </div>
        </section>
      ))}

      {/* 主线延伸：学完两大主打，产品线继续进阶 */}
      <section className="py-20 md:py-24">
        <div className="container-x w-full">
          <SectionHeading eyebrow="主线延伸" title="两条主线之后，继续进阶" desc="神秘电波与点阵魔方是入口。学完主打，同一套硬件语言延伸到闭环控制与机器人。" />
          <div className="mt-10 grid gap-5 md:grid-cols-2">
            {productLines.filter((p) => !/神秘电波|Matrix/i.test(p.name)).map((product, i) => (
              <Link key={product.name} to="/courses" className="product-card reveal" style={{ transitionDelay: `${(i % 2) * 0.06}s` }}>
                <div className="product-card-media">
                  <img src={product.image} alt={product.name} />
                </div>
                <div className="product-card-body">
                  <div className="product-card-eyebrow">{product.eyebrow}</div>
                  <h3>{product.name}</h3>
                  <p>{product.desc}</p>
                  <div className="product-tags">
                    {(product.tags || []).map((tag) => <span key={tag}>{tag}</span>)}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 数据信任条 */}
      {home.results?.stats?.length > 0 && (
        <section className="py-20 md:py-24">
          <div className="container-x w-full">
            <SectionHeading eyebrow={home.results.eyebrow} title={home.results.title} />
            <div className="stats-band reveal">
              {home.results.stats.map((s) => (
                <div key={s.l}>
                  <div className="stat-n"><em>{s.n}</em></div>
                  <div className="stat-l">{s.l}</div>
                </div>
              ))}
            </div>
            {home.results.quote && (
              <blockquote className="stats-quote reveal">
                "{home.results.quote}"
                <footer>—— {home.results.quoteName} · {home.results.quoteRole}</footer>
              </blockquote>
            )}
          </div>
        </section>
      )}

      <section className="py-20 md:py-28">
        <div className="container-x w-full">
          <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-end">
            <div className="reveal">
              <div className="eyebrow mb-4">{proof.eyebrow || '课堂现场'}</div>
              <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight" style={{ color: 'var(--ink)' }}>{proof.title || '从真实项目走进真实课堂'}</h2>
              <p className="mt-5 max-w-2xl text-[17px] leading-relaxed" style={{ color: 'var(--body)' }}>
                {proof.desc || '课程包围绕摩斯电码、电阻屏交互与板球平衡等项目展开，支持机构组织搭建、调试和展示。'}
              </p>
            </div>
            <Link to="/courses" className="link-accent reveal justify-self-start lg:justify-self-end">查看课程体系 <ArrowRight className="h-4 w-4" /></Link>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-3">
            <div className="lg:col-span-2 reveal">
              <img src={proofImages[0].src} alt={proofImages[0].title} className="h-full min-h-[320px] w-full object-cover" style={{ borderRadius: 10 }} />
              <div className="mt-4">
                <h3 className="text-xl font-bold" style={{ color: 'var(--ink)' }}>{proofImages[0].title}</h3>
                <p className="mt-1 text-[15px]" style={{ color: 'var(--body)' }}>{proofImages[0].desc}</p>
              </div>
            </div>
            <div className="grid gap-5">
              {proofImages.slice(1).map((img, i) => (
                <div key={img.src} className="reveal" style={{ transitionDelay: `${(i + 1) * 0.08}s` }}>
                  <img src={img.src} alt={img.title} className="aspect-[16/10] w-full object-contain bg-white" style={{ borderRadius: 10 }} />
                  <h3 className="mt-3 text-[17px] font-bold" style={{ color: 'var(--ink)' }}>{img.title}</h3>
                  <p className="mt-1 text-[14px] leading-relaxed" style={{ color: 'var(--body)' }}>{img.desc}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* 科研背书（猿编程式信任层：来源可信 + 路径清晰） */}
      {home.research && (
        <section className="section-alt py-20 md:py-28">
          <div className="container-x w-full">
            <SectionHeading eyebrow={home.research.eyebrow} title={home.research.title} desc={home.research.desc} />
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {(home.research.points || []).map((pt, i) => {
                const Icon = getIcon(pt.icon);
                return (
                  <div key={pt.title} className="card card-hover reveal" style={{ transitionDelay: `${(i % 4) * 0.06}s` }}>
                    <span className="icon-chip"><Icon className="h-6 w-6" /></span>
                    <h3 className="mt-4 text-[17px] font-bold" style={{ color: 'var(--ink)' }}>{pt.title}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed" style={{ color: 'var(--body)' }}>{pt.desc}</p>
                  </div>
                );
              })}
            </div>
            {home.research.directions?.length > 0 && (
              <div className="mt-10 flex flex-wrap justify-center gap-2.5 reveal">
                {home.research.directions.map((d) => (
                  <span key={d} className="chip" style={{ fontFamily: 'var(--font-mono)', fontSize: '12.5px', letterSpacing: '0.08em' }}>{d}</span>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {news.length > 0 && (
        <section className="section-alt py-20 md:py-28">
          <div className="container-x w-full">
            <div className="flex items-end justify-between gap-4 reveal">
              <div>
                <div className="eyebrow mb-3">新闻中心</div>
                <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight" style={{ color: 'var(--ink)' }}>课程动态与课堂实拍</h2>
              </div>
              <Link to="/news" className="link-accent hidden sm:inline-flex">全部新闻 <ArrowRight className="h-4 w-4" /></Link>
            </div>
            <div className="mt-10 grid gap-6 md:grid-cols-3">
              {news.map((n, i) => (
                <Tilt key={n.id} className="reveal" style={{ transitionDelay: `${i * 0.08}s` }}>
                  <Link to={`/news/${n.slug}`} className="card card-hover block h-full overflow-hidden" style={{ padding: 0 }}>
                    <div className="aspect-[16/9] overflow-hidden" style={{ background: 'var(--bg-alt)' }}>
                      {n.coverImage && <img src={cardImage(n.coverImage)} alt={n.title} className="h-full w-full object-cover transition-transform duration-500 hover:scale-[1.01]" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                    </div>
                    <div className="p-6">
                      <span className="chip">{n.category}</span>
                      <h3 className="mt-3 text-[18px] font-bold leading-snug" style={{ color: 'var(--ink)' }}>{n.title}</h3>
                      <p className="mt-2 text-[14px] leading-relaxed line-clamp-2" style={{ color: 'var(--body)' }}>{n.summary}</p>
                      <div className="mt-3 text-[12px]" style={{ color: 'var(--muted)' }}>{fmt(n.publishDate)}</div>
                    </div>
                  </Link>
                </Tilt>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 md:py-28">
        <div className="container-x w-full">
          <div className="relative overflow-hidden px-6 py-20 text-center" style={{ background: 'var(--cta-bg)', borderRadius: 10 }}>
            <div className="absolute inset-0 morse-texture" />
            <h2 className="relative text-3xl md:text-5xl font-extrabold text-white">{contact.title}</h2>
            <p className="relative mx-auto mt-4 max-w-xl text-[17px] text-white/80">{contact.desc}</p>
            <div className="relative mt-8 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn btn-white">获取合作方案</Link>
              <Link to="/schools" className="btn" style={{ background: 'rgba(255,255,255,0.16)', color: '#fff', border: '1px solid rgba(255,255,255,0.4)' }}>我是学校 / 机构</Link>
            </div>
            <p className="relative mt-8 text-[13px] text-white/80">{site.phone} · {site.email}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
